import React from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import AnimatedText from './AnimatedText';
import FooterLinks from './FooterLinks';
import AppFeaturesMarquee from './AppFeaturesMarquee';
import NumberOneBadge from './NumberOneBadge';
import { Highlighter } from './magicui/highlighter';
import { Iphone } from './magicui/iphone';

interface AppShowcaseProps {
  lang?: 'en' | 'fr' | 'de' | 'it';
}

const AppShowcase: React.FC<AppShowcaseProps> = ({ lang: propLang }) => {
  const { lang: paramLang } = useParams<{ lang?: string }>();
  const currentLang = (propLang || paramLang || 'en') as 'en' | 'fr' | 'de' | 'it';
  
  const translations = {
    en: {
      websitePast: 'The website shows the past',
      appFuture: 'The app shows you the',
      future: 'future',
      playerCount: '500+ Swiss players on the app',
    },
    fr: {
      websitePast: 'Le site web montre le passé',
      appFuture: 'L\'appli te montre le',
      future: 'futur',
      playerCount: '500+ joueurs suisses sur l\'appli',
    },
    de: {
      websitePast: 'Die Website zeigt dir die Vergangenheit',
      appFuture: 'Die App zeigt dir die',
      future: 'Zukunft',
      playerCount: '500+ Schweizer Spieler in der App',
    },
    it: {
      websitePast: 'Il sito web mostra il passato',
      appFuture: 'L\'app ti mostra il',
      future: 'futuro',
      playerCount: '500+ giocatori svizzeri sull\'app',
    }
  };
  
  const t = translations[currentLang] || translations.en;

  return (
    <footer id="app-promotion-section" className="bg-gray-50 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* Minimal adaptive title */}
        <motion.div
          className="mb-12 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}>

          {/* Main tagline - prominent with animated text */}
          <div className="px-4">
            <AnimatedText
              text={t.websitePast}
              className="text-2xl md:text-3xl font-light text-gray-400 dark:text-gray-500 mb-2 max-w-2xl mx-auto transition-colors duration-300"
              delay={0.3}
              staggerDelay={0.02}
            />
            <div className="text-3xl md:text-4xl font-semibold text-gray-900 dark:text-gray-100 max-w-2xl mx-auto transition-colors duration-300">
              <AnimatedText
                text={t.appFuture + ' '}
                className="inline"
                delay={0.6}
                staggerDelay={0.025}
              />
              <span className="inline-block">
                <Highlighter
                  action="highlight"
                  color="#FFD700"
                  strokeWidth={2}
                  animationDuration={1000}
                  padding={4}
                  isView={true}
                >
                  <span className="font-semibold">{t.future}</span>
                </Highlighter>
                <AnimatedText
                  text="."
                  className="inline"
                  delay={1.1}
                  staggerDelay={0}
                />
              </span>
            </div>
          </div>
        </motion.div>

        {/* App Features Marquee with overlapping iPhone */}
        <div className="relative mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.5 }}
            className="mb-8 md:mb-0"
          >
            <AppFeaturesMarquee lang={currentLang} />
          </motion.div>

          {/* iPhone Mockup - overlapping on desktop */}
          <div className="flex justify-center items-center md:absolute md:inset-0 md:z-10">
            <motion.div
              className="w-[180px] md:w-[200px]"
              initial={{ y: 100 }}
              whileInView={{ y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{
                duration: 0.6,
                ease: "easeOut"
              }}
            >
              <Iphone src="/app-screenshot.png" />
            </motion.div>
          </div>
        </div>

        {/* Download CTA */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.3 }}>

          {/* #1 App Badge */}
          <div className="mb-3">
            <NumberOneBadge lang={currentLang} />
          </div>

          <p className="text-[11px] text-gray-400 dark:text-gray-500 mb-5 transition-colors duration-300">{t.playerCount}</p>
          
          {/* App Store Badges */}
          <motion.div 
            className="flex justify-center gap-4 flex-wrap"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.4 }}>
            <a
              href="https://apps.apple.com/ch/app/ttstats-swiss-table-tennis/id6744279609"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-opacity hover:opacity-80"
            >
              <img 
                src="/app-store-badge.svg" 
                alt="Download on App Store" 
                className="h-12"
              />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.peaktwilight.ttstats"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-opacity hover:opacity-80"
            >
              <img 
                src="/google-play-badge.svg" 
                alt="Get it on Google Play" 
                className="h-12"
              />
            </a>
          </motion.div>
        </motion.div>
        
        {/* Footer Links */}
        <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-800 transition-colors duration-300">
          <FooterLinks lang={currentLang} />
        </div>
      </div>
    </footer>
  );
};

export default AppShowcase;